import type { ColumnDef } from '@tanstack/react-table';
import type { WithT } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Goals as GoalsCollection, Segment } from '@feathr/blackbox';
import { type ITanStackColumnMeta, NumberCell, TanStackHeader } from '@feathr/components';

import ConversionPixelCodeCell from './ConversionPixelCodeCell';
import ConversionPixelLastSeenCell from './ConversionPixelLastSeenCell';
import ConversionPixelOptionsCell from './ConversionPixelOptionsCell';

interface IProps extends WithT {
  Goals: GoalsCollection;
}

function ConversionPixelsColumns({ Goals, t }: IProps): Array<ColumnDef<Segment>> {
  return [
    {
      id: 'name',
      // accessorFn is what makes the column sortable — TanStack's getCanSort() is false for a
      // display (accessor-less) column even with global sorting on.
      accessorFn: (row): string => row.name ?? '',
      header: (): JSX.Element => <TanStackHeader title={t('Pixel')} />,
      meta: { isFlex: true, isPrimary: true },
      cell({ row }): JSX.Element {
        return <Observer>{(): JSX.Element => <>{row.original.name}</>}</Observer>;
      },
    },
    {
      id: 'flights',
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Flights')}
          tooltip={t('The number of flights using the pixel for advanced conversion tracking.')}
        />
      ),
      enableSorting: false,
      meta: { align: 'right' },
      size: 120,
      cell({ row }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <NumberCell
                value={Goals.count({
                  filters: {
                    segment: row.original.id,
                    kind: 'flight',
                    is_archived__ne: true,
                  },
                })}
              />
            )}
          </Observer>
        );
      },
    },
    {
      id: 'campaigns',
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Campaigns')}
          tooltip={t('The number of campaigns using the pixel for advanced conversion tracking.')}
        />
      ),
      enableSorting: false,
      meta: { align: 'right' },
      size: 145,
      cell({ row }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <NumberCell
                value={Goals.count({
                  filters: {
                    segment: row.original.id,
                    kind: 'campaign',
                    is_archived__ne: true,
                  },
                })}
              />
            )}
          </Observer>
        );
      },
    },
    {
      id: 'code',
      header: (): JSX.Element => <TanStackHeader title={t('Code')} />,
      enableSorting: false,
      meta: { align: 'center' },
      size: 85,
      cell({ row }): JSX.Element {
        return <ConversionPixelCodeCell segment={row.original} />;
      },
    },
    {
      id: 'timesSeen',
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Times Seen')}
          tooltip={t('The number of times the conversion pixel has successfully tracked activity.')}
        />
      ),
      enableSorting: false,
      meta: { align: 'right' },
      size: 150,
      cell({ row }): JSX.Element {
        return <Observer>{(): JSX.Element => <NumberCell value={row.original.crumbs} />}</Observer>;
      },
    },
    {
      id: 'lastSeen',
      header: (): JSX.Element => (
        <TanStackHeader
          title={t('Last Seen')}
          tooltip={t(
            'The date and time of the most recent activity tracked by the conversion pixel.',
          )}
        />
      ),
      enableSorting: false,
      size: 140,
      cell({ row }): JSX.Element {
        return <ConversionPixelLastSeenCell segment={row.original} />;
      },
    },
    {
      /*
       * Hand-built rather than createActionMenuColumn: the cell must host
       * ConversionPixelOptionsCell, which owns the stateful rename and archive modals rendered
       * outside the menu. Pinned right to match the factory, so the kebab stays reachable while
       * the data columns scroll.
       */
      id: 'options',
      header: (): JSX.Element => <TanStackHeader title={t('Options')} />,
      enableSorting: false,
      meta: { align: 'center', pinned: 'right' } satisfies ITanStackColumnMeta,
      size: 80,
      cell({ row }): JSX.Element {
        // ConversionPixelOptionsCell is already observer-wrapped, so no Observer here.
        return <ConversionPixelOptionsCell segment={row.original} />;
      },
    },
  ];
}

export default ConversionPixelsColumns;
